import { z } from 'zod';
import { DEFAULT_PRINTER_DISCOVERY_SDKS, zPaymentType, zPrinterSdk } from '../enums';
import {
  docketTemplateCvxSchema,
  docketTemplateSchema,
  docketTemplateStrictSchema,
} from './docketTemplateSchema';
import { posPasscodeSchema, posPasscodeStrictSchema } from './posPasscodeSchema';
import {
  posPriceAdjustmentShortcutSchema,
  posPriceAdjustmentShortcutStrictSchema,
} from './posPriceAdjustmentShortcutSchema';
import { posPrinterCvxSchema, posPrinterSchema, posPrinterStrictSchema } from './posPrinterSchema';

export const posConfigSchema = z.object({
  autoAcceptEstWaitTime: z.number().nullable().default(null),
  docketTemplates: z.array(docketTemplateSchema).default([]),
  /**
   * Stable restaurant-level code for the phone docket viewer. Stored raw as a read-only bearer
   * capability so every iPad shares a bookmarkable viewer link.
   */
  docketViewerSessionCode: z.string().optional(),
  openCashDrawerTriggers: z.array(zPaymentType).default([]),
  posPrinters: z.array(posPrinterSchema).default([]),
  /**
   * Which native printer SDK(s) the POS app searches during printer discovery.
   * Discovery always runs sequentially in `PRINTER_SDKS` order (Star Micronics, then Epson),
   * regardless of the order stored here. An empty array disables discovery entirely.
   */
  printerDiscoverySdks: z
    .array(zPrinterSdk)
    .default([...DEFAULT_PRINTER_DISCOVERY_SDKS])
    .optional(),
  /** @deprecated The `print-job-logging` feature flag now. Kept for POS bundles that predate it. */
  printJobLoggingEnabled: z.boolean().optional(),
  restaurantId: z.string(),

  posPasscodes: z.array(posPasscodeSchema).default([]),

  posPriceAdjustmentShortcuts: z.array(posPriceAdjustmentShortcutSchema).default([]),

  /**
   * The uuid of the linked iPad that owns print queues, auto-accept and the venue's open status.
   * A configuration decision, never derived: claimed by the first iPad to link, moved to whichever
   * iPad a USB printer is attached to, and reassigned only when the primary is unlinked.
   * Always a currently-linked iPad's uuid, or null (readers guard against stale values).
   */
  primaryIPadUuid: z.string().nullable().default(null),

  /**
   * Max discount (whole percent of the order/item) non-manager sessions can apply without
   * manager approval. Applies to devices with no passcodes too. Null = every discount
   * requires manager approval.
   */
  staffDiscountPercentLimit: z.number().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type PosConfig = z.output<typeof posConfigSchema>;

export const posConfigStrictSchema = posConfigSchema
  .safeExtend({
    docketTemplates: z.array(docketTemplateStrictSchema),
    posPrinters: z.array(posPrinterStrictSchema),
    posPasscodes: z.array(posPasscodeStrictSchema),
    posPriceAdjustmentShortcuts: z
      .array(posPriceAdjustmentShortcutStrictSchema)
      .max(5, 'A maximum of 5 price adjustment shortcuts is allowed'),
    primaryIPadUuid: z.string().nullable(),
    staffDiscountPercentLimit: z.number().int().min(1).max(100).nullable(),
  })
  /** Validate: A USB printer can only be attached to 1 iPad (each iPad has ≤1 USB printer). */
  .refine(
    (config) => {
      const attachedIPadUuids = config.posPrinters
        .map((printer) => printer.attachedToIPadUuid)
        .filter((uuid): uuid is string => uuid !== null);

      return new Set(attachedIPadUuids).size === attachedIPadUuids.length;
    },
    {
      message: 'Invalid POS configuration: A single iPad has multiple USB printers attached.',
      path: ['posPrinters', 'attachedToIPadUuid'],
    },
  )

  /** Validate: The docket viewer printer is a single row, and never sits beside a physical printer. */
  // TODO: Enable this validation after production deployment.
  // .refine(
  //   (config) => {
  //     const docketViewerPrinterCount = config.posPrinters.filter(isDocketViewerPrinter).length;
  //     const hasPhysicalPrinter = config.posPrinters.some((printer) => !isDocketViewerPrinter(printer));
  //
  //     return docketViewerPrinterCount <= 1 && !(docketViewerPrinterCount > 0 && hasPhysicalPrinter);
  //   },
  //   {
  //     message:
  //       'Invalid POS configuration: The docket viewer can only be set up when there is no printer.',
  //     path: ['posPrinters'],
  //   },
  // )
  /** Validate: No duplicate passcodes allowed */
  .refine(
    (config) => {
      if (config.posPasscodes.length === 0) return true;
      const passcodes = config.posPasscodes.map((p) => p.passcode);
      return new Set(passcodes).size === passcodes.length;
    },
    {
      message: 'Duplicate passcodes are not allowed',
      path: ['posPasscodes'],
    },
  )
  /** Validate: At least one MANAGER required if passcodes exist */
  .refine(
    (config) => {
      if (config.posPasscodes.length === 0) return true;
      return config.posPasscodes.some((p) => p.role === 'MANAGER');
    },
    {
      message: 'At least one MANAGER passcode is required',
      path: ['posPasscodes'],
    },
  )
  /** Validate: No duplicate price adjustment shortcut reasons allowed. Reasons are optional, and
   * any number of shortcuts may go without one, so only the set ones are compared. */
  .refine(
    (config) => {
      const reasons = config.posPriceAdjustmentShortcuts
        .map((shortcut) => shortcut.reason)
        .filter((reason): reason is string => reason !== null);

      return new Set(reasons).size === reasons.length;
    },
    {
      message: 'Duplicate shortcut reasons are not allowed',
      path: ['posPriceAdjustmentShortcuts'],
    },
  );

export const posConfigWithoutRestaurantIdSchema = posConfigSchema.omit({ restaurantId: true });
export type PosConfigWithoutRestaurantId = z.output<typeof posConfigWithoutRestaurantIdSchema>;

export const posConfigCvxSchema = posConfigSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  docketTemplates: z.array(docketTemplateCvxSchema).default([]),
  posPrinters: z.array(posPrinterCvxSchema).default([]),
  updatedAt: z.number(),
});
export type PosConfigCvx = z.output<typeof posConfigCvxSchema>;
